<script>
    // Step 2: the four things the engine needs that a photo cannot tell it.
    //
    // These questions used to sit below the primary button on the results
    // screen, so most people never answered them and the routine was built on
    // defaults: age 25 (which decides whether an eye cream gets a slot), skin
    // type guessed from the detected concerns, and no declared sensitivities,
    // which is a safety filter left empty.
    //
    // Everything here is pre-filled from the analysis, so the honest path for
    // someone with nothing to correct is one press of Continue.
    import { onMount } from 'svelte';
    import { navigate } from '../router.js';
    import Button from '../components/Button.svelte';
    import Card from '../components/Card.svelte';
    import Field from '../components/Field.svelte';
    import {
        CONCERN_OPTIONS,
        SENSITIVITY_OPTIONS,
        loadProfile,
        inferSkinType,
        saveProfileToServer,
        persistProfile
    } from '../profile.js';

    let selectedConcerns = [];
    let skinType = 'normal';
    let age = null;
    let skinAllergies = [];
    let routineType = 'mid_tier';
    let hasAnalysis = true;
    let hasRoutine = false;
    let skinTypeTouched = false;
    let isSaving = false;
    let statusMessage = '';

    onMount(() => {
        const profile = loadProfile();
        selectedConcerns = [...profile.areasOfImprovement];
        skinType = profile.skinType;
        age = profile.age;
        skinAllergies = [...profile.skinAllergies];
        routineType = profile.routineType;
        skinTypeTouched = profile.answered;
        hasAnalysis = selectedConcerns.length > 0;
        hasRoutine = (() => {
            try { return (JSON.parse(localStorage.getItem('currentRoutine') || '[]') || []).length > 0; }
            catch (e) { return false; }
        })();
    });

    // Until the person picks a skin type themselves, it keeps following the
    // concerns they tick. Once they choose, their answer stands.
    $: if (!skinTypeTouched) skinType = inferSkinType(selectedConcerns);

    function answers() {
        return {
            areasOfImprovement: selectedConcerns,
            skinType,
            age,
            skinAllergies,
            routineType,
            answered: true
        };
    }

    async function saveAndContinue() {
        isSaving = true;
        statusMessage = '';
        try {
            await saveProfileToServer(answers());
        } catch (error) {
            // The profile is stored in this browser either way, so the flow
            // carries on and the routine is still built from these answers.
            console.error('Could not save the skin profile:', error);
            persistProfile(answers());
        }
        isSaving = false;

        if (hasRoutine) {
            localStorage.removeItem('onboardingStep');
        } else {
            localStorage.setItem('onboardingStep', 'routine');
        }
        navigate('routine-import');
    }

    function backToResults() {
        persistProfile(answers());
        navigate('face-analysis-result');
    }

    function startAnalysis() {
        navigate('face-analysis');
    }

    function toTitleCase(value) {
        return (value || '').replace(/\w\S*/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    }
</script>

<div class="u-page questions">
    <header>
        <p class="eyebrow">{hasRoutine ? 'Your skin details' : 'Step 2 of 4 · About your skin'}</p>
        <h1>A few things the photo can’t tell us.</h1>
        <p class="lead">
            We’ve filled these in from your analysis. Change anything that looks wrong,
            then continue.
        </p>
    </header>

    {#if !hasAnalysis}
        <Card tone="caution">
            <p class="muted">
                We don’t have an analysis saved in this browser, so nothing here is
                pre-filled. You can answer anyway, or take a photo first.
            </p>
            <Button variant="secondary" on:click={startAnalysis}>Analyze my skin</Button>
        </Card>
    {/if}

    <section aria-labelledby="concerns-title">
        <h2 id="concerns-title">What should your routine work on?</h2>
        <p class="helper">
            Ticked from your photo. Add anything we missed or untick anything that isn’t a
            concern for you.
        </p>
        <div class="option-grid">
            {#each CONCERN_OPTIONS as concern}
                <label class="option">
                    <input type="checkbox" bind:group={selectedConcerns} value={concern} />
                    <span>{toTitleCase(concern)}</span>
                </label>
            {/each}
        </div>
    </section>

    <section aria-labelledby="details-title">
        <h2 id="details-title">Your skin, in your own words</h2>
        <p class="helper">
            Age changes what a routine should include, so it is worth getting right.
        </p>
        <div class="detail-fields">
            <Field label="Skin type" let:id>
                <select {id} bind:value={skinType} on:change={() => skinTypeTouched = true}>
                    <option value="normal">Normal</option>
                    <option value="dry">Dry</option>
                    <option value="oily">Oily</option>
                    <option value="combination">Combination</option>
                </select>
            </Field>
            <Field label="Age" let:id>
                <select {id} bind:value={age}>
                    <option value={null}>Select your age</option>
                    {#each Array(87) as _, i}
                        <option value={i + 14}>{i + 14}</option>
                    {/each}
                </select>
            </Field>
            <Field label="Routine style" hint="How many steps you want." let:id let:describedBy>
                <select {id} aria-describedby={describedBy} bind:value={routineType}>
                    <option value="minimalist">Minimalist</option>
                    <option value="mid_tier">Balanced</option>
                    <option value="maximalist">Maximalist</option>
                </select>
            </Field>
        </div>
    </section>

    <section aria-labelledby="sensitivities-title">
        <h2 id="sensitivities-title">Anything your skin reacts to?</h2>
        <p class="helper">
            We’ll leave these ingredients out of your routine. Skip it if nothing applies.
            If you’re pregnant, breastfeeding, or using prescription treatments, check with
            your doctor or dermatologist before starting anything new.
        </p>
        <p class="helper">
            We match against published ingredient lists, so this only works where we have
            them. We can’t reliably screen for fragrance yet, so if that’s your concern,
            please check the label yourself.
        </p>
        <div class="option-grid">
            {#each SENSITIVITY_OPTIONS as option}
                <label class="option">
                    <input type="checkbox" bind:group={skinAllergies} value={option.value} />
                    <span>{option.label}</span>
                </label>
            {/each}
        </div>
        {#if skinAllergies.length > 0}
            <p class="helper avoiding">
                Avoiding {skinAllergies.length} ingredient group{skinAllergies.length === 1 ? '' : 's'} in everything we suggest.
            </p>
        {/if}
    </section>

    <div class="actions">
        <Button on:click={saveAndContinue} loading={isSaving} busyLabel="Saving">
            {hasRoutine ? 'Save my details' : 'Continue'}
        </Button>
        <Button variant="ghost" on:click={backToResults}>Back to my results</Button>
    </div>
    <p class="status" aria-live="polite">{statusMessage}</p>

    <div class="phone-back">
        <Button variant="ghost" on:click={backToResults}>Back to my results</Button>
    </div>

    <div class="cta-bar">
        <Button full on:click={saveAndContinue} loading={isSaving} busyLabel="Saving">
            {hasRoutine ? 'Save my details' : 'Continue'}
        </Button>
    </div>
</div>

<style>
    .questions { font-family: var(--font-body); }

    .questions > * + * {
        margin-top: var(--space-12);
    }

    header { max-width: var(--prose-max); }

    .eyebrow {
        color: var(--sage);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
    }

    h1 {
        font-family: var(--font-display);
        font-weight: var(--weight-regular);
        font-size: var(--text-4xl);
        line-height: var(--leading-tight);
        margin: var(--space-3) 0 var(--space-4);
    }

    h2 {
        font-family: var(--font-display);
        font-weight: var(--weight-regular);
        font-size: var(--text-xl);
        margin: 0 0 var(--space-2);
    }

    .lead, .helper, .muted {
        color: var(--text-muted);
        line-height: var(--leading-relaxed);
    }

    .helper { max-width: var(--prose-max); margin: 0 0 var(--space-5); }
    .muted { margin: 0 0 var(--space-5); }

    .option-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: var(--space-2);
    }

    .option {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        color: var(--cream);
        border: 1px solid var(--border);
        padding: var(--space-3);
        background: var(--surface);
        font-size: var(--text-xs);
        cursor: pointer;
        transition: border-color var(--transition), background var(--transition);
    }

    .option:hover {
        border-color: var(--border-strong);
        background: var(--surface-2);
    }

    .option input {
        accent-color: var(--rose);
        width: auto;
        margin: 0;
    }

    .detail-fields {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 0 var(--space-4);
    }

    /* The selects arrive through Field's slot, so they need :global. */
    .detail-fields :global(select) {
        background: var(--bg);
        border-radius: var(--radius-0);
        padding: var(--space-3);
        font-size: var(--text-sm);
    }

    .avoiding { color: var(--sage); margin: var(--space-4) 0 0; }

    .actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-3);
    }

    .status {
        color: var(--sage);
        min-height: 1.5em;
        margin: var(--space-3) 0 0;
    }

    /* Phone: the same primary action, within thumb reach. Matches the bar on
       the results screen, so the two steps feel like one flow. */
    .cta-bar, .phone-back { display: none; }

    @media (max-width: 768px) {
        .actions { display: none; }
        .phone-back { display: block; }

        .cta-bar {
            display: block;
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: var(--z-menu);
            padding: var(--space-3) var(--space-4) calc(var(--space-3) + env(safe-area-inset-bottom, 0));
            background: var(--scrim);
            backdrop-filter: blur(18px);
            border-top: 1px solid var(--border);
        }

        .questions {
            padding-bottom: 128px;
        }
    }
</style>
